import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

const DoeMeePage = () => {
  const [formData, setFormData] = useState({
    naam: '',
    email: '',
    telefoon: '',
    rol: '',
    bericht: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const body = new URLSearchParams({
        naam: formData.naam,
        email: formData.email,
        telefoon: formData.telefoon,
        rol: formData.rol,
        bericht: formData.bericht,
      });

      const response = await fetch('https://readdy.ai/api/form/d76r5erq7u9ee9hcekf0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json',
        },
        body: body.toString(),
      });

      if (response.ok || response.status === 201 || response.status === 302) {
        setSubmitMessage('Bedankt! Je aanmelding is succesvol verzonden.');
        setFormData({
          naam: '',
          email: '',
          telefoon: '',
          rol: '',
          bericht: ''
        });
      } else {
        setSubmitMessage('Er is een fout opgetreden. Probeer het opnieuw of neem contact op via info@herstart.nl.');
      }
    } catch (error) {
      setSubmitMessage('Verbindingsfout. Probeer het opnieuw of neem direct contact op via info@herstart.nl.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const manieren = [
    {
      titel: 'Donateur worden',
      icon: 'ri-heart-line',
      beschrijving: 'Steun onze missie met een maandelijkse of eenmalige donatie',
      details: [
        'Vanaf €10 per maand maak je al verschil',
        'Eenmalige donaties zijn ook welkom',
        'Transparante besteding van donaties',
        'Jaarlijks impact rapport'
      ],
      actie: 'Doneer Nu',
      link: '/contact?onderwerp=donateur',
      kleur: 'purple'
    },
    {
      titel: 'Vrijwilliger worden',
      icon: 'ri-team-line',
      beschrijving: 'Deel je tijd en talenten met jongeren die het nodig hebben',
      details: [
        'Mentorschap en begeleiding',
        'Ondersteuning bij trainingen',
        'Administratieve hulp',
        'Evenementen organiseren'
      ],
      actie: 'Meld je Aan',
      link: '/contact?onderwerp=vrijwilliger',
      kleur: 'green'
    },
    {
      titel: 'Bedrijfspartner',
      icon: 'ri-building-line',
      beschrijving: 'Maatschappelijk verantwoord ondernemen met impact',
      details: [
        'Sponsoring van programma\'s',
        'Werkplekken voor jongeren',
        'Expertise en netwerk delen',
        'Team building met impact'
      ],
      actie: 'Neem Contact Op',
      link: '/contact?onderwerp=bedrijfspartner',
      kleur: 'blue'
    },
    {
      titel: 'Ambassadeur',
      icon: 'ri-megaphone-line',
      beschrijving: 'Verspreid ons verhaal en vergroot onze impact',
      details: [
        'Deel onze missie op social media',
        'Organiseer fondswervingsacties',
        'Spreek over ons in je netwerk',
        'Word onderdeel van ons verhaal'
      ],
      actie: 'Word Ambassadeur',
      link: '/contact?onderwerp=ambassadeur',
      kleur: 'orange'
    }
  ];

  const impactcijfers = [
    {
      cijfer: '500+',
      beschrijving: 'Jongeren geholpen',
      icon: 'ri-user-heart-line'
    },
    {
      cijfer: '85%',
      beschrijving: 'Succesvol terug naar school',
      icon: 'ri-graduation-cap-line'
    },
    {
      cijfer: '25+',
      beschrijving: 'Partner organisaties',
      icon: 'ri-links-line'
    },
    {
      cijfer: '€250K',
      beschrijving: 'Geïnvesteerd in jongeren',
      icon: 'ri-funds-line'
    }
  ];

  const verhalen = [
    {
      naam: 'Maria van der Berg',
      rol: 'Vrijwilliger mentor',
      verhaal: 'Als gepensioneerd docent wilde ik mijn ervaring nog steeds inzetten voor jongeren. Bij HerStart kan ik echt het verschil maken.',
      foto: 'volunteer-maria'
    },
    {
      naam: 'TechStart Nederland',
      rol: 'Bedrijfspartner',
      verhaal: 'Door onze samenwerking met HerStart hebben we al 15 jongeren een stageplek kunnen bieden. Geweldig om hun groei te zien.',
      foto: 'partner-techstart'
    },
    {
      naam: 'Ahmed Hassan',
      rol: 'Ambassadeur',
      verhaal: 'Zelf ben ik via HerStart teruggekeerd naar school. Nu help ik andere jongeren door mijn verhaal te delen.',
      foto: 'ambassador-ahmed'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Sectie */}
      <section 
        className="relative py-32 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/7d1737e45a66739e7aceb703b0addef7.jpg')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/85 via-gray-900/60 to-transparent"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Doe Mee &amp; Steun Ons
            </h1>
            <p className="text-xl md:text-2xl text-gray-100 leading-relaxed">
              Sluit je aan bij HerStart en help ons jongeren een nieuwe start te geven. 
              Samen bouwen we aan een toekomst vol kansen.
            </p>
          </div>
        </div>
      </section>

      {/* Impact Cijfers */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Onze Impact Tot Nu Toe
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Dankzij de steun van onze donateurs, vrijwilligers en partners 
              hebben we al veel jongeren kunnen helpen.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {impactcijfers.map((impact, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${impact.icon} text-orange-500 text-2xl`}></i>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  {impact.cijfer}
                </div>
                <p className="text-gray-600">
                  {impact.beschrijving}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Manieren om te Helpen */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Manieren om te Helpen
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Er zijn verschillende manieren waarop je kunt bijdragen aan onze missie. 
              Kies wat het beste bij jou past.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {manieren.map((manier, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className={`w-12 h-12 bg-${manier.kleur}-100 rounded-lg flex items-center justify-center mr-4`}>
                    <i className={`${manier.icon} text-${manier.kleur}-600 text-xl`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {manier.titel}
                  </h3>
                </div>
                <p className="text-gray-600 mb-6">
                  {manier.beschrijving}
                </p>
                <div className="space-y-2 mb-6">
                  {manier.details.map((detail, dIndex) => (
                    <div key={dIndex} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                      <span className="text-gray-700 text-sm">{detail}</span>
                    </div>
                  ))}
                </div>
                <Button 
                  href={manier.link} 
                  variant={manier.kleur === 'purple' ? 'primary' : 'secondary'} 
                  size="md"
                  className="w-full"
                >
                  {manier.actie}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Aanmeldformulier */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Meld je aan
              </h2>
              <p className="text-lg text-gray-600">
                Ben je een jongere, ouder, vrijwilliger of organisatie die wil meedoen? 
                Vul het formulier in en we nemen binnen 24 uur contact met je op.
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form="herstart-aanmelding"
              className="bg-gray-50 rounded-2xl p-6 sm:p-8 space-y-6"
            >
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="naam" className="block text-sm font-medium text-gray-700 mb-2">
                    Naam *
                  </label>
                  <input
                    type="text"
                    id="naam"
                    name="naam"
                    value={formData.naam}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                    placeholder="Je volledige naam"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    E-mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                    placeholder="jouw@email.nl"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="telefoon" className="block text-sm font-medium text-gray-700 mb-2">
                    Telefoon
                  </label>
                  <input
                    type="tel"
                    id="telefoon"
                    name="telefoon"
                    value={formData.telefoon}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                    placeholder="06 12345678"
                  />
                </div>

                <div>
                  <label htmlFor="rol" className="block text-sm font-medium text-gray-700 mb-2">
                    Ik ben een... *
                  </label>
                  <select
                    id="rol"
                    name="rol"
                    value={formData.rol}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                  >
                    <option value="">Kies een optie</option>
                    <option value="jongere">Jongere die hulp zoekt</option>
                    <option value="ouder">Ouder/verzorger</option>
                    <option value="vrijwilliger">Vrijwilliger</option>
                    <option value="gemeente">Gemeente/school</option>
                    <option value="bedrijf">Bedrijf</option>
                    <option value="anders">Anders</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="bericht" className="block text-sm font-medium text-gray-700 mb-2">
                  Vertel ons meer
                </label>
                <textarea
                  id="bericht"
                  name="bericht"
                  value={formData.bericht}
                  onChange={handleInputChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none bg-white"
                  placeholder="Waar kunnen we je mee helpen?"
                />
                <p className="text-sm text-gray-500 mt-1">
                  {formData.bericht.length}/500 karakters
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-orange-500 text-white py-4 px-8 rounded-lg font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Bezig met verzenden...' : 'Verstuur aanmelding'}
              </button>

              {submitMessage && (
                <div className={`p-4 rounded-lg ${
                  submitMessage.includes('succesvol')
                    ? 'bg-green-50 text-green-800 border border-green-200'
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {submitMessage}
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Verhalen van Supporters */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Verhalen van Onze Supporters
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Lees waarom anderen hebben gekozen om HerStart te steunen 
              en wat het voor hen betekent.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {verhalen.map((verhaal, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-3">
                    <i className="ri-user-line text-orange-500 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{verhaal.naam}</h4>
                    <p className="text-sm text-gray-600">{verhaal.rol}</p>
                  </div>
                </div>
                <div className="mb-4">
                  <i className="ri-double-quotes-l text-orange-500 text-xl"></i>
                </div>
                <p className="text-gray-700 italic">
                  "{verhaal.verhaal}"
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Donatie Sectie */}
      <section className="py-16 bg-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Elke Euro Telt
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Met jouw donatie kunnen we meer jongeren bereiken en hen de begeleiding 
                bieden die ze nodig hebben om terug te keren naar het onderwijs.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-4">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                    <span className="text-orange-500 font-bold text-sm">€10</span>
                  </div>
                  <span className="text-gray-700">Financiert één individuele begeleidingssessie</span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                    <span className="text-orange-500 font-bold text-sm">€25</span>
                  </div>
                  <span className="text-gray-700">Betaalt voor trainingsmaterialen voor één jongere</span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                    <span className="text-orange-500 font-bold text-sm">€50</span>
                  </div>
                  <span className="text-gray-700">Maakt deelname aan volledige training mogelijk</span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                    <span className="text-orange-500 font-bold text-sm">€100</span>
                  </div>
                  <span className="text-gray-700">Ondersteunt een jongere gedurende het hele traject</span>
                </div>
              </div>

              <Button href="/contact?onderwerp=donateur" variant="primary" size="lg">
                Doneer Nu
              </Button>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Happy%20young%20student%20receiving%20support%20and%20guidance%2C%20showing%20gratitude%20and%20hope%2C%20bright%20educational%20environment%20with%20books%20and%20learning%20materials%2C%20positive%20impact%20of%20donations%20and%20support&width=600&height=400&seq=herstart-donatie-impact&orientation=landscape"
                alt="Impact van donaties"
                className="rounded-lg shadow-lg object-cover w-full h-80"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Nieuwsbrief */}
      <section className="py-16 bg-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Blijf op de Hoogte
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Ontvang onze nieuwsbrief en lees over de impact die we samen maken, 
            nieuwe programma's en succesverhalen van jongeren.
          </p>
          
          <div className="max-w-md mx-auto">
            <div className="flex flex-col sm:flex-row gap-4">
              <input 
                type="email" 
                placeholder="Je e-mailadres"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <Button variant="secondary" size="md" className="whitespace-nowrap">
                Aanmelden
              </Button>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              We respecteren je privacy en sturen maximaal 1x per maand een update.
            </p>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-orange-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Begin Vandaag met Helpen
          </h2>
          <p className="text-xl text-orange-100 mb-8 max-w-3xl mx-auto">
            Elke vorm van steun, groot of klein, maakt een verschil in het leven 
            van een jongere. Sluit je vandaag nog aan bij onze missie.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button href="/contact?onderwerp=anders" variant="secondary" size="lg">
              Neem Contact Op
            </Button>
            <Button href="/over-herstart" variant="outline" size="lg" className="bg-white text-orange-500 border-white hover:bg-orange-50">
              Lees Ons Verhaal
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DoeMeePage;
